import React, { useState, useRef } from 'react'
import { ExclamationTriangleIcon, CheckCircleIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import ReCAPTCHA from 'react-google-recaptcha'
import SEO from '@components/common/SEO'

interface ReportForm {
  category: string
  companyName: string
  issueTitle: string
  description: string
  email: string
  screenshot: File | null
}

const ReportPage: React.FC = () => {
  const [formData, setFormData] = useState<ReportForm>({
    category: '',
    companyName: '',
    issueTitle: '',
    description: '',
    email: '',
    screenshot: null
  })

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof ReportForm, string>>>({})
  const [submitError, setSubmitError] = useState('')
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const categories = [
    { value: '', label: 'เลือกประเภทปัญหา' },
    { value: 'incorrect-info', label: 'ข้อมูลบริษัทไม่ถูกต้อง' },
    { value: 'technical-issue', label: 'ปัญหาทางเทคนิค' },
    { value: 'inappropriate-content', label: 'เนื้อหาไม่เหมาะสม' },
    { value: 'claim-issue', label: 'ปัญหาการ Claim บริษัท' },
    { value: 'security-concern', label: 'ปัญหาด้านความปลอดภัย' },
    { value: 'other', label: 'อื่นๆ' }
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof ReportForm]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, screenshot: 'ไฟล์มีขนาดใหญ่เกิน 5MB' }))
        return
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, screenshot: 'กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น' }))
        return
      }
      setFormData(prev => ({ ...prev, screenshot: file }))
      setErrors(prev => ({ ...prev, screenshot: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ReportForm, string>> = {}

    if (!formData.category) {
      newErrors.category = 'กรุณาเลือกประเภทปัญหา'
    }

    if (!formData.issueTitle.trim()) {
      newErrors.issueTitle = 'กรุณาระบุหัวข้อปัญหา'
    } else if (formData.issueTitle.trim().length < 5) {
      newErrors.issueTitle = 'หัวข้อต้องมีอย่างน้อย 5 ตัวอักษร'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'กรุณาอธิบายรายละเอียดปัญหา'
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'รายละเอียดต้องมีอย่างน้อย 20 ตัวอักษร'
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) {
      return
    }

    // Verify reCAPTCHA
    const recaptchaValue = recaptchaRef.current?.getValue()
    if (!recaptchaValue) {
      setSubmitError('กรุณายืนยันว่าคุณไม่ใช่โปรแกรมอัตโนมัติ')
      return
    }

    setIsSubmitting(true)

    try {
      // Prepare FormData for file upload
      const submitData = new FormData()
      submitData.append('category', formData.category)
      submitData.append('companyName', formData.companyName)
      submitData.append('issueTitle', formData.issueTitle)
      submitData.append('description', formData.description)
      submitData.append('email', formData.email)
      submitData.append('recaptchaToken', recaptchaValue)

      if (formData.screenshot) {
        submitData.append('screenshot', formData.screenshot)
      }

      // Send to backend API
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:7004'}/api/report/submit`, {
        method: 'POST',
        body: submitData
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setIsSubmitted(true)
        recaptchaRef.current?.reset()

        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false)
          setFormData({
            category: '',
            companyName: '',
            issueTitle: '',
            description: '',
            email: '',
            screenshot: null
          })
        }, 3000)
      } else {
        setSubmitError(data.message || 'เกิดข้อผิดพลาดในการส่งรายงาน')
      }
    } catch (error) {
      console.error('Report submission error:', error)
      setSubmitError('เกิดข้อผิดพลาดในการส่งรายงาน กรุณาลองใหม่อีกครั้ง')
    } finally {
      setIsSubmitting(false)
    }
  }

  const reportStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "รายงานปัญหา - ไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "description": "แจ้งปัญหา ข้อมูลผิดพลาด หรือข้อกังวลต่างๆ เกี่ยวกับเว็บไซต์"
  }

  if (isSubmitted) {
    return (
      <>
        <SEO title="รายงานปัญหา" />
        <div className="bg-gradient-to-b from-green-50 to-white min-h-screen flex items-center justify-center py-12">
          <div className="container-wrapper">
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-lg shadow-lg border border-green-200 p-12">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                  ส่งรายงานสำเร็จ!
                </h2>
                <p className="text-neutral-700 text-lg mb-2">
                  ขอบคุณสำหรับการรายงานปัญหา
                </p>
                <p className="text-neutral-600">
                  เราจะตรวจสอบและดำเนินการแก้ไขโดยเร็วที่สุด
                </p>
                <p className="text-sm text-neutral-500 mt-6">
                  {formData.email && `เราจะติดต่อกลับไปที่ ${formData.email} หากจำเป็น`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title="รายงานปัญหา"
        description="แจ้งปัญหา ข้อมูลผิดพลาด หรือข้อกังวลต่างๆ เกี่ยวกับไทยเซ็กคิวริตี้ไดเรกทอรี่ - เราพร้อมรับฟังและแก้ไข"
        keywords="รายงานปัญหา, report issue, แจ้งปัญหา, ข้อมูลผิดพลาด"
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(reportStructuredData) }}
      />

      <div className="bg-gradient-to-b from-red-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-red-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <ExclamationTriangleIcon className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                รายงานปัญหา
              </h1>
              <p className="text-xl text-red-100">
                พบข้อมูลผิดพลาดหรือปัญหาในการใช้งาน? แจ้งเราได้ที่นี่
              </p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="container-wrapper py-12">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-8">
              <p className="text-neutral-700 leading-relaxed">
                เราให้ความสำคัญกับคุณภาพของข้อมูลและประสบการณ์การใช้งาน
                หากคุณพบปัญหาใดๆ ไม่ว่าจะเป็นข้อมูลผิดพลาด ปัญหาทางเทคนิค หรือเนื้อหาไม่เหมาะสม
                กรุณาแจ้งให้เราทราบ เราจะตรวจสอบและแก้ไขโดยเร็วที่สุด
              </p>
            </div>

            {/* Form */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-semibold text-neutral-900 mb-2">
                    ประเภทปัญหา <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${
                      errors.category ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                  )}
                </div>

                {/* Company Name (Optional) */}
                <div>
                  <label htmlFor="companyName" className="block text-sm font-semibold text-neutral-900 mb-2">
                    ชื่อบริษัทที่เกี่ยวข้อง (ถ้ามี)
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="เช่น บริษัท ไทยเซ็กคิวริตี้ จำกัด"
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  />
                </div>

                {/* Issue Title */}
                <div>
                  <label htmlFor="issueTitle" className="block text-sm font-semibold text-neutral-900 mb-2">
                    หัวข้อปัญหา <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="issueTitle"
                    name="issueTitle"
                    value={formData.issueTitle}
                    onChange={handleChange}
                    placeholder="สรุปปัญหาโดยย่อ"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${
                      errors.issueTitle ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  />
                  {errors.issueTitle && (
                    <p className="mt-1 text-sm text-red-600">{errors.issueTitle}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-neutral-900 mb-2">
                    รายละเอียดปัญหา <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={6}
                    placeholder="อธิบายปัญหาที่พบอย่างละเอียด เช่น เมื่อไหร่ ที่ไหน เกิดอะไรขึ้น"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors resize-none ${
                      errors.description ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    ขั้นต่ำ 20 ตัวอักษร ({formData.description.length}/20)
                  </p>
                </div>

                {/* Email (Optional) */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-neutral-900 mb-2">
                    อีเมลของคุณ (ถ้าต้องการให้เราติดต่อกลับ)
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${
                      errors.email ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Screenshot Upload (Optional) */}
                <div>
                  <label htmlFor="screenshot" className="block text-sm font-semibold text-neutral-900 mb-2">
                    แนบภาพหน้าจอ (ถ้ามี)
                  </label>
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="screenshot"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-neutral-100 text-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-200 transition-colors border border-neutral-300"
                    >
                      <PaperClipIcon className="w-5 h-5" />
                      <span className="text-sm font-medium">เลือกไฟล์</span>
                    </label>
                    <input
                      type="file"
                      id="screenshot"
                      name="screenshot"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="hidden"
                    />
                    {formData.screenshot && (
                      <span className="text-sm text-neutral-600">
                        {formData.screenshot.name}
                      </span>
                    )}
                  </div>
                  {errors.screenshot && (
                    <p className="mt-1 text-sm text-red-600">{errors.screenshot}</p>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    รองรับไฟล์ JPG, PNG, GIF ขนาดไม่เกิน 5MB
                  </p>
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                  />
                </div>

                {/* Error Message */}
                {submitError && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-6 py-3.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-md ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'กำลังส่ง...' : 'ส่งรายงานปัญหา'}
                  </button>
                </div>

                {/* Privacy Note */}
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    ข้อมูลที่คุณส่งมาจะถูกใช้เพื่อวัตถุประสงค์ในการแก้ไขปัญหาเท่านั้น
                    เราจะไม่เปิดเผยข้อมูลส่วนตัวของคุณให้บุคคลที่สาม
                    อ่านเพิ่มเติมที่ <a href="/privacy" className="text-red-600 hover:underline">นโยบายความเป็นส่วนตัว</a>
                  </p>
                </div>
              </form>
            </div>

            {/* Additional Help */}
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <h3 className="font-bold text-blue-900 mb-2">ต้องการความช่วยเหลือทันที?</h3>
              <p className="text-blue-800 text-sm mb-3">
                หากเป็นปัญหาเร่งด่วน กรุณาติดต่อเราโดยตรง:
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  ติดต่อเรา
                </a>
                <a
                  href="/faq"
                  className="inline-flex items-center justify-center px-4 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors border border-blue-300 text-sm"
                >
                  คำถามที่พบบ่อย
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReportPage
